import React, { useState, useEffect } from 'react';
import { View, StyleSheet, FlatList, TouchableOpacity, RefreshControl } from 'react-native';
import { Text, Card, Searchbar, ActivityIndicator, Chip, Divider } from 'react-native-paper';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import api from '../../services/api';
import { calcularProximaVisita } from '../../utils/rutaHelpers';

// Normaliza texto para búsqueda: minúsculas y sin acentos. Necesario porque
// los nombres de clientes vienen con tildes ("García") y el usuario tipea
// sin ellas ("garcia"); String.includes falla sin esta normalización.
const normalizar = (s) => (s || '')
  .toString()
  .toLowerCase()
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '');

const RutasScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const insets = useSafeAreaInsets();
  const [searchQuery, setSearchQuery] = useState('');
  const [rutas, setRutas] = useState([]);
  const [filteredRutas, setFilteredRutas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filterType, setFilterType] = useState('todas');

  const loadRutas = async (showLoader = true) => {
    try {
      if (showLoader) {
        setLoading(true);
      }
      // Obtener datos del backend
      const data = await api.getRutas();
      console.log('Rutas cargadas:', data.length);
      console.log('Datos completos de rutas:', JSON.stringify(data, null, 2));
      
      // Formatear los datos para la vista
      const rutasFormateadas = [];
      
      // Procesar cada ruta y obtener datos adicionales si es necesario
      for (const ruta of data) {
        console.log(`Procesando ruta ${ruta.id}...`);
        
        // 1. Obtener datos detallados de la ruta (incluye vendedor y total de clientes)
        let rutaDetallada;
        try {
          // Intentar obtener datos detallados de la ruta
          rutaDetallada = await api.getRuta(ruta.id);
          console.log(`Datos detallados de ruta ${ruta.id}:`, rutaDetallada);
        } catch (error) {
          console.error(`Error al obtener detalles de ruta ${ruta.id}:`, error);
          rutaDetallada = ruta; // Usar datos básicos si falla
        }
        
        // 2. Determinar el nombre del vendedor
        let vendedorNombre = 'Sin asignar';
        if (rutaDetallada.usuario_nombre) {
          vendedorNombre = rutaDetallada.usuario_nombre;
        } else if (ruta.usuario_nombre) {
          vendedorNombre = ruta.usuario_nombre;
        }
        
        // 3. Determinar el total de clientes y precomputar string para buscar por nombre.
        // El backend devuelve los clientes con alias `cliente_nombre` (ver rutas.js:45).
        let totalClientes = 0;
        let clientesIndex = '';
        if (rutaDetallada.clientes && Array.isArray(rutaDetallada.clientes)) {
          totalClientes = rutaDetallada.clientes.length;
          clientesIndex = rutaDetallada.clientes
            .map(c => normalizar(c.cliente_nombre || c.nombre || ''))
            .join(' ');
        }

        // 4. Calcular próxima visita (helper compartido con HomeScreen)
        const proximaVisita = calcularProximaVisita(ruta.dias_visita);
        
        // 5. Log para verificar el procesamiento
        console.log(`Ruta ${ruta.id}: ${vendedorNombre}, ${totalClientes} clientes, próxima: ${proximaVisita}`);
        
        // 6. Crear objeto formateado para la vista
        rutasFormateadas.push({
          id: ruta.id,
          nombre: ruta.nombre,
          vendedor: vendedorNombre,
          clientes: totalClientes,
          clientesIndex,
          estado: ruta.estado || 'activa',
          ultimaVisita: ruta.ultima_visita ? new Date(ruta.ultima_visita).toISOString().split('T')[0] : null,
          proximaVisita: proximaVisita,
          descripcion: ruta.descripcion,
          diasVisita: ruta.dias_visita
        });
      }
      
      setRutas(rutasFormateadas);
      setFilteredRutas(rutasFormateadas);
      setLoading(false);
      setRefreshing(false);
    } catch (error) {
      console.error('Error al cargar rutas:', error);
      setLoading(false);
      setRefreshing(false);
      // Si hay un error, mostrar algunas rutas de ejemplo para desarrollo
      const rutasEjemplo = [
        { 
          id: 1, 
          nombre: 'Ruta Norte (ejemplo)', 
          vendedor: 'Juan Pérez', 
          clientes: 12, 
          estado: 'activa',
          ultimaVisita: '2025-06-10',
          proximaVisita: '2025-06-17'
        },
        { 
          id: 2, 
          nombre: 'Ruta Sur (ejemplo)', 
          vendedor: 'María López', 
          clientes: 8, 
          estado: 'activa',
          ultimaVisita: '2025-06-08',
          proximaVisita: '2025-06-15'
        }
      ];
      setRutas(rutasEjemplo);
      setFilteredRutas(rutasEjemplo);
    }
  };

  // Cargar rutas al inicializar la pantalla
  useEffect(() => {
    loadRutas();
  }, []);

  // Escuchar cuando la pantalla reciba el foco y verificar parámetros
  useFocusEffect(
    React.useCallback(() => {
      // Verificar si se pasó el parámetro refresh
      if (route.params?.refresh) {
        console.log('Refrescando rutas después de crear nueva ruta');
        // No mostrar loader completo, solo refrescar datos
        loadRutas(false);
        
        // Limpiar el parámetro para evitar refrescar múltiples veces
        navigation.setParams({ refresh: undefined, newRutaId: undefined });
      }
    }, [route.params?.refresh])
  );

  const onRefresh = () => {
    setRefreshing(true);
    loadRutas();
  };

  const matchQuery = (ruta, query) => {
    const q = normalizar(query);
    if (!q) return true;
    return (
      normalizar(ruta.nombre).includes(q) ||
      normalizar(ruta.vendedor).includes(q) ||
      (ruta.clientesIndex || '').includes(q)
    );
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.trim() === '') {
      applyFilters(filterType);
    } else {
      setFilteredRutas(rutas.filter(r => matchQuery(r, query)));
    }
  };

  const applyFilters = (type) => {
    setFilterType(type);
    let filtered = [...rutas];

    switch (type) {
      case 'activas':
        filtered = filtered.filter((ruta) => ruta.estado === 'activa');
        break;
      case 'inactivas':
        filtered = filtered.filter((ruta) => ruta.estado === 'inactiva');
        break;
      default:
        // 'todas' - no filter
        break;
    }

    if (searchQuery.trim() !== '') {
      filtered = filtered.filter(r => matchQuery(r, searchQuery));
    }

    setFilteredRutas(filtered);
  };

  const renderRutaItem = ({ item }) => (
    <TouchableOpacity
      onPress={() => navigation.navigate('DetalleRuta', { rutaId: item.id })}
    >
      <Card style={styles.card}>
        <Card.Content>
          <View style={styles.rutaHeader}>
            <View>
              <Text style={styles.rutaName}>{item.nombre}</Text>
              <Text style={styles.vendedorName}>Vendedor: {item.vendedor}</Text>
            </View>
            <Chip 
              mode="outlined"
              style={[
                styles.estadoChip,
                item.estado === 'activa' ? styles.chipActiva : styles.chipInactiva
              ]}
            >
              {item.estado === 'activa' ? 'Activa' : 'Inactiva'}
            </Chip>
          </View>
          <Divider style={styles.divider} />
          <View style={styles.rutaFooter}>
            <Text>Clientes: {item.clientes}</Text>
            <Text>
              Próxima visita: {item.proximaVisita || 'No programada'}
            </Text>
          </View>
        </Card.Content>
      </Card>
    </TouchableOpacity>
  );

  return (
    <View style={styles.container}>
      <Text style={styles.helperBlock}>
        Las rutas agrupan clientes con un plan de visita semanal. La pantalla de inicio te avisa qué rutas toca visitar hoy según los días que programes.
      </Text>

      <Searchbar
        placeholder="Buscar por ruta, vendedor o cliente"
        onChangeText={handleSearch}
        value={searchQuery}
        style={styles.searchBar}
      />

      <View style={styles.filterContainer}>
        {[
          { id: 'todas', label: 'Todas' },
          { id: 'activas', label: 'Activas' },
          { id: 'inactivas', label: 'Inactivas' },
        ].map(f => (
          <Chip
            key={f.id}
            onPress={() => applyFilters(f.id)}
            style={[styles.filterChip, filterType === f.id && styles.filterChipActive]}
            textStyle={filterType === f.id ? styles.filterChipTextActive : styles.filterChipTextInactive}
          >
            {f.label}
          </Chip>
        ))}
      </View>

      {loading ? (
        <View style={styles.loaderContainer}>
          <ActivityIndicator size="large" color="#0066cc" />
          <Text style={styles.loaderText}>Cargando rutas...</Text>
        </View>
      ) : (
        <>
          <FlatList
            data={filteredRutas}
            renderItem={renderRutaItem}
            keyExtractor={(item) => item.id.toString()}
            contentContainerStyle={styles.listContainer}
            refreshControl={
              <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
            }
            ListEmptyComponent={
              <View style={styles.emptyContainer}>
                <Text style={styles.emptyText}>No se encontraron rutas</Text>
              </View>
            }
          />

          <TouchableOpacity
            style={[styles.fab, { bottom: 20 + insets.bottom }]}
            onPress={() => navigation.navigate('NuevaRuta')}
          >
            <Ionicons name="add" size={24} color="white" />
          </TouchableOpacity>
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    padding: 10,
  },
  helperBlock: {
    fontSize: 12,
    color: '#555',
    backgroundColor: '#e8f4fd',
    borderLeftWidth: 3,
    borderLeftColor: '#0066cc',
    padding: 8,
    marginBottom: 10,
    lineHeight: 17,
  },
  searchBar: {
    marginBottom: 10,
    backgroundColor: 'white',
  },
  filterContainer: {
    flexDirection: 'row',
    marginBottom: 10,
  },
  filterChip: {
    marginRight: 8,
    backgroundColor: '#f0f0f0',
  },
  filterChipActive: {
    backgroundColor: '#4CAF50',
  },
  filterChipTextInactive: {
    color: '#333',
  },
  filterChipTextActive: {
    color: 'white',
    fontWeight: '600',
  },
  card: {
    marginBottom: 10,
    elevation: 2,
  },
  rutaHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  rutaName: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  vendedorName: {
    fontSize: 14,
    color: '#666',
  },
  estadoChip: {
    height: 28,
  },
  chipActiva: {
    backgroundColor: '#ccffcc',
  },
  chipInactiva: {
    backgroundColor: '#ffcccc',
  },
  divider: {
    marginVertical: 8,
  },
  rutaFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  loaderContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loaderText: {
    marginTop: 10,
  },
  listContainer: {
    paddingBottom: 80,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 50,
  },
  emptyText: {
    fontSize: 16,
    color: '#666',
  },
  fab: {
    position: 'absolute',
    right: 20,
    bottom: 20,
    backgroundColor: '#0066cc',
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 4,
  },
});

export default RutasScreen;
